import { useEffect } from 'react';

import gql from 'graphql-tag';

import { useQueryLight } from '@atlaskit/teams-client/use-query-light/use-query-light';
import { failUFOExperience, logErrorMessage, logException } from '@atlassian/ptc-common';
import { getConfig } from '@atlassian/teams-common/context-config';
import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
} from '@atlassian/ufo-experimental/experience-types';
import { UFOExperience } from '@atlassian/ufo-experimental/experience';

import { useSiteStandaloneApp } from '../site-standalone-app';

interface OrganizationIdState {
	orgId: string | undefined;
	isLoading: boolean;
}

interface OrganizationIdQueryResponse {
	tenantContexts: {
		orgId: string;
	}[];
}
interface OrganizationIdQueryVariables {
	cloudIds: string[];
}

const query = gql`
	query ptc_orgId($cloudIds: [ID!]) {
		tenantContexts(cloudIds: $cloudIds) {
			orgId
		}
	}
`;

const orgIdQueryExperience = new UFOExperience('query-organization-id', {
	type: ExperienceTypes.Load,
	performanceType: ExperiencePerformanceTypes.PageSegmentLoad,
	platform: { component: 'people' },
});

// `useOrgIdQuery` is used in standalone to fetch orgId from AGG and then it is passed to Embeddable Directory.
const useOrgIdQuery = (metadata = {}): OrganizationIdState => {
	const [{ cloudId, hasLoaded }] = useSiteStandaloneApp();

	const skip = !cloudId;
	const { data, loading, error } = useQueryLight<
		OrganizationIdQueryVariables,
		OrganizationIdQueryResponse
	>(query, {
		serviceUrl: `${getConfig().stargateRoot}/graphql`,
		variables: {
			cloudIds: [cloudId],
		},
		skip,
		operationName: 'ptc_orgId',
	});

	useEffect(() => {
		if (!cloudId && hasLoaded) {
			logErrorMessage('No cloudId provided to useOrgIdQuery');
		}
	}, [cloudId, hasLoaded]);

	useEffect(() => {
		if (!skip) {
			void orgIdQueryExperience.start();
		}

		return () => {
			if (!skip) {
				void orgIdQueryExperience.abort();
			}
		};
	}, [skip]);

	useEffect(() => {
		if (error) {
			logException(error, 'Could not load organizationId');
			failUFOExperience(orgIdQueryExperience, error, metadata);
		}
	}, [error, metadata]);

	const orgId = data?.tenantContexts?.[0]?.orgId;

	useEffect(() => {
		if (data) {
			void orgIdQueryExperience.success({
				metadata: {
					...metadata,
					hasOrgId: Boolean(orgId),
				},
			});

			if (!orgId) {
				logErrorMessage('Invalid orgId returned from useOrgIdQuery');
			}
		}
	}, [data, metadata, orgId]);

	return {
		orgId,
		isLoading: loading,
	};
};

export default useOrgIdQuery;
